import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  ArrowBack, 
  Edit, 
  Email, 
  Phone, 
  Business, 
  LocationOn,
  CalendarToday,
  Person
} from '@mui/icons-material';
import { bookkeepersAPI } from '../services/api';

const BookkeeperDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bookkeeper, setBookkeeper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBookkeeper();
  }, [id]);

  const fetchBookkeeper = async () => {
    try {
      setLoading(true);
      setError('');
      
      // console.log('🔍 Fetching bookkeeper with ID:', id);
      
      // Use the correct API path - /admin/bookkeepers/:id
      // console.log('📋 Fetching full bookkeeper details...');
      const response = await bookkeepersAPI.getById(id);
      
      if (response.data.success) {
        // console.log('✅ Bookkeeper data received:', response.data.data);
        setBookkeeper(response.data.data);
      } else {
        setError('Failed to load bookkeeper details');
      }
    } catch (err) {
      console.error('❌ Error fetching bookkeeper:', err);
      console.error('Error details:', err.response?.data);
      
      if (err.response?.status === 404) {
        setError('Bookkeeper not found. It may have been deleted.');
      } else if (err.response?.status === 400) {
        setError('Invalid bookkeeper ID format');
      } else if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to load bookkeeper details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isExpired = () => {
    if (!bookkeeper?.expirationDate) return false;
    return new Date(bookkeeper.expirationDate) < new Date();
  };

  const handleRetry = () => {
    fetchBookkeeper();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Bookkeeper Details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/bookkeepers')}>
          Back to Bookkeepers
        </Button>
      </Container>
    );
  }

  if (!bookkeeper) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Bookkeeper not found
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/bookkeepers')}>
          Back to Bookkeepers
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {bookkeeper.fullName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip 
                label={bookkeeper.status} 
                color={getStatusColor(bookkeeper.status)}
                size="small"
              />
              {bookkeeper.status === 'approved' && isExpired() && (
                <Chip label="Expired" color="error" size="small" />
              )}
              {bookkeeper.status === 'approved' && !isExpired() && (
                <Chip label="Active" color="success" size="small" />
              )}
              {bookkeeper.isVerified && (
                <Chip label="Verified" color="info" size="small" />
              )}
              {bookkeeper.referralSource === 'ICADLA' && (
                <Chip label="ICADLA" color="secondary" size="small" />
              )}
            </Box>
          </Box>
          
          <Button 
            variant="outlined" 
            startIcon={<Edit />}
            onClick={() => navigate(`/bookkeepers/${id}/edit`)}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Personal Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1 }} />
              Personal Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Full Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.fullName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email sx={{ fontSize: 16, mr: 0.5 }} />
                  Email
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.email || 'Not provided'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ fontSize: 16, mr: 0.5 }} />
                  Phone
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.phone || 'Not provided'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Preferred Contact
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.preferredContact ? bookkeeper.preferredContact.charAt(0).toUpperCase() + bookkeeper.preferredContact.slice(1) : 'Email'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Address Information */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationOn sx={{ mr: 1 }} />
              Address Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Address
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.address || 'Not provided'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  City
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.city || 'Not provided'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  State
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.state || 'Not provided'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  ZIP Code
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.zipCode || 'Not provided'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Column - Professional Information */}
        <Grid item xs={12} md={6}>
          {/* Company Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Business sx={{ mr: 1 }} />
              Company Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Company Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.companyName || 'Not provided'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Company Address
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.companyAddress || 'Not provided'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Bio
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: bookkeeper.bio ? 'normal' : 'italic' }}>
                  {bookkeeper.bio || 'No bio provided'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Experience (years)
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.experience ? `${bookkeeper.experience} years` : 'Not specified'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Hourly Rate
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bookkeeper.hourlyRate ? `$${bookkeeper.hourlyRate}/hr` : 'Not specified'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Qualifications & Specialties */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Qualifications & Specialties
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Qualifications
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {bookkeeper.qualifications && bookkeeper.qualifications.length > 0 ? (
                    bookkeeper.qualifications.map((qual, index) => (
                      <Chip key={index} label={qual} size="small" variant="outlined" />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary" fontStyle="italic">
                      No qualifications listed
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Specialties
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {bookkeeper.specialties && bookkeeper.specialties.length > 0 ? (
                    bookkeeper.specialties.map((specialty, index) => (
                      <Chip key={index} label={specialty} size="small" color="primary" />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary" fontStyle="italic">
                      No specialties listed
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Status & Dates */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarToday sx={{ mr: 1 }} />
              Status & Dates
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Created
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {formatDate(bookkeeper.createdAt)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Last Updated
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {formatDate(bookkeeper.updatedAt)}
                </Typography>
              </Grid>

              {bookkeeper.approvedAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Approved Date
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {formatDate(bookkeeper.approvedAt)}
                  </Typography>
                </Grid>
              )}

              {bookkeeper.expirationDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Expiration Date
                  </Typography>
                  <Typography 
                    variant="body2" 
                    gutterBottom
                    color={isExpired() ? 'error' : 'inherit'}
                    fontWeight={isExpired() ? 'bold' : 'normal'}
                  >
                    {formatDate(bookkeeper.expirationDate)}
                    {isExpired() && ' (Expired)'}
                  </Typography>
                </Grid>
              )}

              {bookkeeper.approvedBy && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Approved By
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {bookkeeper.approvedBy.username} ({bookkeeper.approvedBy.email})
                  </Typography>
                </Grid>
              )}

              {bookkeeper.rejectionReason && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Rejection Reason
                  </Typography>
                  <Typography variant="body2" gutterBottom color="error">
                    {bookkeeper.rejectionReason}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BookkeeperDetail;